import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { getUserFeedbackDashboard } from "../../utils/public-admin-dashboard-functions";
import Sidebar from "../Sidebar";

const getEmptyFeedbackSummary = () => ({
  totalRatings: 0,
  avgOverallRating: 0,
});

export default function UserFeedback({ setAuth, setUserOrg }) {
  const [startDate, setStartDate] = useState("");
  const [feedbackRows, setFeedbackRows] = useState([]);
  const [feedbackSummary, setFeedbackSummary] = useState(getEmptyFeedbackSummary());
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(true);
  const [feedbackError, setFeedbackError] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [sortConfig, setSortConfig] = useState({
    key: "appt_date",
    direction: "desc",
  });

  useEffect(() => {
    (async () => {
      setIsFeedbackLoading(true);
      setFeedbackError("");
      try {
        const data = await getUserFeedbackDashboard(startDate);
        setFeedbackRows(data.feedback || []);
        setFeedbackSummary(data.summary || getEmptyFeedbackSummary());
        setAppliedEndDate(data.appliedEndDate || format(new Date(), "yyyy-MM-dd"));
      } catch (error) {
        console.error("Error loading user feedback:", error);
        setFeedbackRows([]);
        setFeedbackSummary(getEmptyFeedbackSummary());
        setFeedbackError("Unable to load user feedback.");
      } finally {
        setIsFeedbackLoading(false);
      }
    })();
  }, [startDate]);

  const formattedDate = (value) => {
    if (!value) return "N/A";
    try {
      const dateObj = new Date(value);
      if (Number.isNaN(dateObj.getTime())) return value;
      return format(dateObj, "MM/dd/yyyy");
    } catch {
      return value;
    }
  };

  const exportCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (/[",\n]/.test(stringValue)) return `"${stringValue.replace(/"/g, '""')}"`;
    return stringValue;
  };

  const sortedFeedbackRows = useMemo(() => {
    if (!Array.isArray(feedbackRows) || feedbackRows.length === 0) return [];

    const normalizeDate = (value) => {
      if (!value) return 0;
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    };

    return [...feedbackRows].sort((a, b) => {
      const key = sortConfig.key;
      const direction = sortConfig.direction === "asc" ? 1 : -1;

      if (key === "rating" || key === "avg_doctor_rating") {
        const aValue = Number(a?.[key] ?? 0);
        const bValue = Number(b?.[key] ?? 0);
        return (aValue - bValue) * direction;
      }

      if (key === "patient_id") {
        const aNum = Number(a?.patient_id);
        const bNum = Number(b?.patient_id);
        const aIsNum = Number.isFinite(aNum);
        const bIsNum = Number.isFinite(bNum);

        if (aIsNum && bIsNum) return (aNum - bNum) * direction;
        if (aIsNum) return -1 * direction;
        if (bIsNum) return 1 * direction;
      }

      if (key === "appt_date") {
        const aDate = normalizeDate(a?.appt_date);
        const bDate = normalizeDate(b?.appt_date);
        return (aDate - bDate) * direction;
      }

      const aValue = String(a?.[key] ?? "").toLowerCase();
      const bValue = String(b?.[key] ?? "").toLowerCase();
      return aValue.localeCompare(bValue) * direction;
    });
  }, [feedbackRows, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const handleExportFeedbackCsv = () => {
    if (!sortedFeedbackRows.length) return;
    const headers = [
      "Doctor",
      "Specialty",
      "Organization",
      "Super Org",
      "Patient ID",
      "Date of Appt",
      "Rating",
      "Doctor Avg",
      "Overall Avg",
      "Feedback",
    ];
    const rows = sortedFeedbackRows.map((entry) => [
      entry.user_name || "",
      entry.specialty || "",
      entry.organization || "",
      entry.super_org || "",
      entry.patient_id || "",
      formattedDate(entry.appt_date),
      entry.rating ?? "",
      entry.avg_doctor_rating ?? "",
      entry.avg_overall_rating ?? "",
      entry.feedback || "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(exportCsvValue).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const startLabel = startDate || "all-time";
    link.setAttribute("href", url);
    link.setAttribute("download", `commercial-user-feedback-${startLabel}-to-${appliedEndDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full min-h-screen bg-white text-dark-purple font-sans flex">
      <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
      <div
        className="flex flex-col gap-4 pb-6 transition-all duration-200"
        style={{
          marginLeft: "var(--sidebar-width, 250px)",
          width: "calc(100% - var(--sidebar-width, 250px))",
        }}
      >
        <div className="w-[90%] mx-auto mt-8 items-center">
          <h2 className="text-2xl font-bold">User Feedback</h2>
        </div>

        <h1 className="font-sans w-[90%] mx-auto text-[36px] font-black">User Feedback Dashboard</h1>

        <div className="flex flex-col w-[90%] mx-auto gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-semibold text-ktx-navy">Start Date</label>
            <input
              type="date"
              className="rounded-md bg-white shadow-knowtex-shadow h-[2.5rem] px-3 text-ktx-navy"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <button
              className="font-semibold hover:bg-ktx-navy rounded-md bg-ktx-purple tracking-wide text-white py-[.65rem] px-4"
              onClick={() => setStartDate("")}
              disabled={!startDate}
            >
              Clear Filter
            </button>
            <p className="text-sm text-ktx-navy">
              Range: <span className="font-semibold">{startDate || "All Time"}</span> to{" "}
              <span className="font-semibold">{appliedEndDate}</span>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-light-purple rounded-md p-4">
              <p className="text-xs uppercase tracking-[.2em] text-ktx-navy">Total Star Ratings</p>
              <p className="text-3xl font-bold text-ktx-purple">
                {isFeedbackLoading ? "..." : feedbackSummary.totalRatings || 0}
              </p>
            </div>
            <div className="bg-light-purple rounded-md p-4">
              <p className="text-xs uppercase tracking-[.2em] text-ktx-navy">Average Overall Rating</p>
              <p className="text-3xl font-bold text-ktx-purple">
                {isFeedbackLoading
                  ? "..."
                  : feedbackSummary.totalRatings > 0
                  ? Number(feedbackSummary.avgOverallRating || 0).toFixed(2)
                  : "N/A"}
              </p>
            </div>
          </div>
        </div>

        <div className="w-[90%] mx-auto bg-white rounded-md shadow-knowtex-shadow overflow-hidden">
          <div className="bg-dark-purple text-white px-4 py-2 font-semibold flex items-center justify-between">
            <span>Detailed User Feedback</span>
            <button
              onClick={handleExportFeedbackCsv}
              disabled={!sortedFeedbackRows.length}
              className={`px-3 py-1 rounded-md text-xs font-semibold ${
                sortedFeedbackRows.length
                  ? "bg-white text-ktx-purple hover:bg-light-purple"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Export CSV
            </button>
          </div>
          {feedbackError ? (
            <div className="p-4 text-red-600">{feedbackError}</div>
          ) : !sortedFeedbackRows.length ? (
            <div className="p-4 text-gray-500">No feedback found for this date range.</div>
          ) : (
            <div className="max-h-[58dvh] overflow-y-auto">
              <table className="w-full bg-[#FBF9FF]">
                <thead className="sticky top-0 bg-light-purple">
                  <tr>
                    <th className="px-4 py-2 text-left cursor-pointer select-none" onClick={() => handleSort("user_name")}>
                      Doctor {getSortIndicator("user_name")}
                    </th>
                    <th className="px-4 py-2 text-left cursor-pointer select-none" onClick={() => handleSort("specialty")}>
                      Specialty {getSortIndicator("specialty")}
                    </th>
                    <th className="px-4 py-2 text-left cursor-pointer select-none" onClick={() => handleSort("organization")}>
                      Organization {getSortIndicator("organization")}
                    </th>
                    <th className="px-4 py-2 text-left cursor-pointer select-none" onClick={() => handleSort("super_org")}>
                      Super Org {getSortIndicator("super_org")}
                    </th>
                    <th className="px-4 py-2 text-left cursor-pointer select-none" onClick={() => handleSort("patient_id")}>
                      Patient ID {getSortIndicator("patient_id")}
                    </th>
                    <th className="px-4 py-2 text-left cursor-pointer select-none" onClick={() => handleSort("appt_date")}>
                      Date of Appt {getSortIndicator("appt_date")}
                    </th>
                    <th className="px-4 py-2 text-left cursor-pointer select-none" onClick={() => handleSort("rating")}>
                      Rating {getSortIndicator("rating")}
                    </th>
                    <th className="px-4 py-2 text-left cursor-pointer select-none" onClick={() => handleSort("avg_doctor_rating")}>
                      Doctor Avg {getSortIndicator("avg_doctor_rating")}
                    </th>
                    <th className="px-4 py-2 text-left cursor-pointer select-none" onClick={() => handleSort("feedback")}>
                      Feedback {getSortIndicator("feedback")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFeedbackRows.map((entry, index) => (
                    <tr key={`${entry.user_name || "doctor"}-${entry.patient_id || index}`} className="border-b">
                      <td className="px-4 py-2">{entry.user_name || "—"}</td>
                      <td className="px-4 py-2">{entry.specialty || "—"}</td>
                      <td className="px-4 py-2">{entry.organization || "—"}</td>
                      <td className="px-4 py-2">{entry.super_org || "—"}</td>
                      <td className="px-4 py-2">{entry.patient_id || "—"}</td>
                      <td className="px-4 py-2">{formattedDate(entry.appt_date)}</td>
                      <td className="px-4 py-2 font-semibold">{entry.rating ?? "—"}</td>
                      <td className="px-4 py-2">{entry.avg_doctor_rating ?? "—"}</td>
                      <td className="px-4 py-2">{entry.feedback || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
